import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StarRating } from "@/components/StarRating";
import WriteReviewDialog from "@/components/WriteReviewDialog";

const IMG = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL || ""}${u}`);

function ReviewCard({ r }) {
  return (
    <article className="border-b border-[#E5E2DC] py-6" data-testid="review-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-8 h-8 rounded-full bg-[#1A1C1A] text-[#F9F9F6] flex items-center justify-center font-serif text-xs">
          {r.author_initial}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1A1C1A]">{r.author_name}</p>
          {r.verified_purchase && <p className="text-[10px] uppercase tracking-[0.15em] text-[#3F5F3F]">Verified purchase</p>}
        </div>
        <div className="ml-auto text-[11px] text-stone-400">
          {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
        </div>
      </div>
      <StarRating value={r.rating} />
      {r.title && <h4 className="mt-2 font-serif text-lg font-light">{r.title}</h4>}
      <p className="mt-2 text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{r.body}</p>
      {r.image_urls?.length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {r.image_urls.map((u, i) => (
            <a key={i} href={IMG(u)} target="_blank" rel="noreferrer">
              <img src={IMG(u)} alt="" className="w-20 h-20 object-cover rounded-md" />
            </a>
          ))}
        </div>
      )}
      {r.owner_reply && (
        <div className="mt-4 border-l-2 border-[#1A1C1A] pl-4 py-2 bg-[#F4F1EA] rounded-r" data-testid="review-owner-reply">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#3F5F3F] font-semibold mb-1">Reply from Jenova</p>
          <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{r.owner_reply}</p>
        </div>
      )}
    </article>
  );
}

export default function ProductReviewsSection({ productId, productName }) {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [writeOpen, setWriteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reviews?product_id=${productId}&limit=6`);
      setReviews(data.items || []);
      setSummary(data.summary || { count: 0, average: 0 });
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="mt-24 border-t border-[#E5E2DC] pt-16" data-testid="product-reviews-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-semibold mb-2">What customers say</p>
          <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight text-[#1A1C1A]">Reviews</h2>
          {summary.count > 0 && (
            <div className="mt-3 flex items-center gap-3">
              <StarRating value={Math.round(summary.average)} size={20} />
              <span className="text-sm text-stone-600">{summary.average.toFixed(1)} · {summary.count} review{summary.count === 1 ? "" : "s"}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="open-write-review"
            onClick={() => setWriteOpen(true)}
            className="rounded-full bg-[#1A1C1A] hover:bg-[#3C3E3C]"
          >
            Write a review
          </Button>
          {summary.count > 0 && (
            <Link to={`/reviews?product_id=${productId}`} data-testid="product-view-all-reviews">
              <Button variant="outline" className="rounded-full">View all →</Button>
            </Link>
          )}
        </div>
      </div>

      {loading && <p className="text-sm text-stone-500 italic">Loading reviews…</p>}
      {!loading && reviews.length === 0 && (
        <div className="py-12 text-center border border-dashed border-[#E5E2DC] rounded-xl">
          <p className="font-serif text-xl font-light italic text-stone-500">No reviews yet — be the first.</p>
        </div>
      )}
      {!loading && reviews.map((r) => <ReviewCard key={r.id} r={r} />)}

      <WriteReviewDialog
        open={writeOpen}
        onOpenChange={setWriteOpen}
        productId={productId}
        productName={productName}
        onSubmitted={load}
      />
    </section>
  );
}
